// RIASEC result and program matching, shared by the chat (and testable on its own).
// Scoring is unchanged: 42 Yes/No questions, +1 per Yes to the question's type, 0-7 per type.

import { riasecQuestions } from '../../assessments/riasecQuestions';
import onet from './onetDrafts.json';
import { isHiddenProgram, twinOf, visibleCatalog } from './programCatalog';

export const TYPE_ORDER = ['realistic', 'investigative', 'artistic', 'social', 'enterprising', 'conventional'];

// Everyday names a 16-year-old reads first; the official name stays small beside them
export const TYPES = {
  realistic: { everyday: 'Hands-on', official: 'Realistic', meaning: 'You like building, fixing and working with tools or nature.' },
  investigative: { everyday: 'Thinking', official: 'Investigative', meaning: 'You like figuring out how things work.' },
  artistic: { everyday: 'Creative', official: 'Artistic', meaning: 'You like making things and sharing ideas.' },
  social: { everyday: 'People', official: 'Social', meaning: 'You like helping and working with others.' },
  enterprising: { everyday: 'Leading', official: 'Enterprising', meaning: 'You like leading, persuading and selling.' },
  conventional: { everyday: 'Organizing', official: 'Conventional', meaning: 'You like order, numbers and clear steps.' },
};

export const MAX_PER_TYPE = 7;

/** Six scores from the answers ('yes' / 'no' by question index). */
export const scoreAnswers = (answers = []) => {
  const scores = Object.fromEntries(TYPE_ORDER.map((t) => [t, 0]));
  riasecQuestions.forEach((q, i) => {
    if (answers[i] === 'yes' && q.type in scores) scores[q.type] += 1;
  });
  return scores;
};

/** All six, highest first; equal scores keep the R-I-A-S-E-C order only for listing. */
export const rankTypes = (scores) =>
  [...TYPE_ORDER].sort((a, b) => (scores[b] || 0) - (scores[a] || 0) || TYPE_ORDER.indexOf(a) - TYPE_ORDER.indexOf(b));

export const topThree = (scores) => rankTypes(scores).slice(0, 3);

/** The code the Conversation record stores: [[type, score], …] for the top three (format unchanged). */
export const riasecCodeOf = (scores) => topThree(scores).map((t) => [t, scores[t] || 0]);

/**
 * How clear the result is, in words a student understands:
 * low   - said yes to very few things;  flat - answers spread out evenly;
 * tied  - the top three are within a point; clear - otherwise.
 */
export const clarityOf = (scores) => {
  const ranked = rankTypes(scores).map((t) => scores[t] || 0);
  const highest = ranked[0];
  const lowest = ranked[ranked.length - 1];
  if (highest <= 2) return 'low';
  if (highest - lowest <= 2) return 'flat';
  if (highest - ranked[2] <= 1) return 'tied';
  return 'clear';
};

// ---------- strands ----------

/**
 * The one strand list for the whole app (chat, Profile, Add user, Import, sign-up). `value` is
 * what the server stores (custom_auth/strands.py keeps the same list), `label` what people read,
 * `fits` the interests the strand is built on ('open' for GAS), `key` the short name used inside
 * the RIASEC logic. Stored text is matched without regard to case (strandKey / strandValue).
 */
export const STRANDS = [
  { key: 'ABM', value: 'ABM', label: 'ABM', fits: ['enterprising', 'conventional'] },
  { key: 'STEM', value: 'STEM', label: 'STEM', fits: ['investigative', 'realistic'] },
  { key: 'HUMSS', value: 'HUMSS', label: 'HUMSS', fits: ['social', 'artistic', 'enterprising'] },
  { key: 'ARTS', value: 'ARTS & DESIGN', label: 'Arts and Design', fits: ['artistic'] },
  { key: 'GAS', value: 'GAS', label: 'GAS', fits: 'open' },
  { key: 'TVL_ICT', value: 'TVL - Information and Communications Technology', label: 'TVL – ICT', fits: ['investigative', 'realistic', 'conventional'] },
  { key: 'TVL_HE', value: 'TVL - Home Economics', label: 'TVL – Home Economics', fits: ['realistic', 'social'] },
  { key: 'TVL_AFA', value: 'TVL - Agri-Fishery Arts', label: 'TVL – Agri-Fishery Arts', fits: ['realistic', 'investigative'] },
  { key: 'TVL_IA', value: 'TVL - Industrial Arts', label: 'TVL – Industrial Arts', fits: ['realistic', 'conventional'] },
];

const STRAND_BY_KEY = Object.fromEntries(STRANDS.map((s) => [s.key, s]));

/** Profile and course strand text in any case ("TVL - HOME ECONOMICS", "tvl – ICT", "STEM" …) → a STRANDS key. */
export const strandKey = (value) => {
  const s = String(value || '').toUpperCase().replace(/[–—]/g, '-').replace(/\s+/g, ' ').trim();
  if (!s || s === 'N/A' || s === 'NA' || s === 'NONE') return null;
  if (STRAND_BY_KEY[s]) return s;
  if (s.includes('ARTS') && s.includes('DESIGN')) return 'ARTS';
  if (s.startsWith('TVL') || s.includes('INFORMATION AND COMMUNICATION') || s === 'ICT' || s.includes('HOME ECONOMICS') || s.includes('AGRI') || s.includes('INDUSTRIAL')) {
    if (s.includes('INFORMATION') || /\bICT\b/.test(s)) return 'TVL_ICT';
    if (s.includes('HOME') || /\bHE\b/.test(s)) return 'TVL_HE';
    if (s.includes('AGRI') || /\bAFA\b/.test(s)) return 'TVL_AFA';
    if (s.includes('INDUSTRIAL') || /\bIA\b/.test(s)) return 'TVL_IA';
    return null;
  }
  if (s.startsWith('ABM')) return 'ABM';
  if (s.startsWith('STEM')) return 'STEM';
  if (s.startsWith('HUMSS')) return 'HUMSS';
  if (s.startsWith('GAS')) return 'GAS';
  return null;
};

export const strandLabel = (key) => (key && STRAND_BY_KEY[key] ? STRAND_BY_KEY[key].label : '');

/** The stored value for any strand text ('' when it isn't one), so a select can show what was saved. */
export const strandValue = (value) => {
  const key = strandKey(value);
  return key ? STRAND_BY_KEY[key].value : '';
};

/** The interests a strand is built on (a STRANDS key), 'open' for GAS, [] when unknown. */
export const strandFits = (key) => (key && STRAND_BY_KEY[key] ? STRAND_BY_KEY[key].fits : []);

/**
 * How the student's interests sit with their strand.
 * none: no strand on file · skip: the result is too unclear to judge · open: GAS
 * fits: the strand's interests include the first interest · partly: the second or third · diff: none of the three
 */
export const strandVerdict = ({ top, strand, clarity }) => {
  if (!strand || !STRAND_BY_KEY[strand]) return 'none';
  if (clarity === 'flat' || clarity === 'low') return 'skip';
  const fits = STRAND_BY_KEY[strand].fits;
  if (fits === 'open') return 'open';
  if (fits.includes(top[0])) return 'fits';
  if (fits.includes(top[1]) || fits.includes(top[2])) return 'partly';
  return 'diff';
};

// ---------- programs ----------

const LETTER = { R: 'realistic', I: 'investigative', A: 'artistic', S: 'social', E: 'enterprising', C: 'conventional' };

/** A course's riasec_area entries ("ARTISTIC", "A", "Artistic") → type keys. */
export const courseTypes = (course) =>
  (Array.isArray(course?.riasec_area) ? course.riasec_area : [])
    .map((v) => String(v || '').trim())
    .map((v) => (v.length === 1 ? LETTER[v.toUpperCase()] : v.toLowerCase()))
    .filter((v) => TYPE_ORDER.includes(v));

export const courseStrands = (course) =>
  [...new Set((Array.isArray(course?.strand) ? course.strand : []).map(strandKey).filter(Boolean))];

export const normalizeName = (name) => String(name || '').trim().toLowerCase();

const WEIGHTS = [3, 2, 1];

const TIER_RANK = { great: 3, good: 2, look: 1 };

const toTypes = (letters) =>
  [...new Set((Array.isArray(letters) ? letters : []).map((v) => LETTER[String(v || '').trim().toUpperCase()]).filter(Boolean))].slice(0, 3);

/**
 * ANNA's default interest code for a program, read from the O*NET job most of its graduates go
 * into (scripts/riasec-codes/build_onet_drafts.py): { code: ['S', 'I', 'C'], onet_soc, onet_title }
 * or null when no O*NET job fits.
 */
export const defaultCodeOf = (name) => onet.drafts[String(name || '').trim()] || null;
export const ONET_VERSION = onet.onet_version;

/**
 * A program's interest code as type keys, strongest first: the school's own code when it set one
 * (see withSchoolCodes), else ANNA's default from O*NET, else [].
 */
export const courseCode = (course) => {
  const own = toTypes(course?.riasec_code);
  return own.length ? own : toTypes(defaultCodeOf(course?.name)?.code);
};

/**
 * The catalog with each school's own codes in place: a program the school offers and gave its
 * own code (offering.riasec_code, set in Management → Degree programs) uses that code.
 */
export const withSchoolCodes = (courses = [], offerings = []) => {
  const own = new Map(
    (Array.isArray(offerings) ? offerings : [])
      .filter((o) => o && Array.isArray(o.riasec_code) && o.riasec_code.length)
      .map((o) => [normalizeName(o.course_name), o.riasec_code]),
  );
  if (!own.size) return courses;
  return courses.map((course) => (own.has(normalizeName(course.name)) ? { ...course, riasec_code: own.get(normalizeName(course.name)) } : course));
};

/**
 * True when any program has a code: matching then uses the C index below for every program.
 * There is no share to reach first: a program with a code compares by its code, and one without
 * (a new catalog entry with no default yet) falls back to its own single tags on the same 0-18
 * scale (codeFitOf), so one uncoded program never switches the whole list back to tags.
 */
export const codesReady = (courses = []) => courses.some((course) => courseCode(course).length > 0);

// Closeness of two types on Holland's hexagon (R-I-A-S-E-C-R): same 3, next to each other 2,
// one apart 1, opposite 0
const closeness = (a, b) => {
  const d = Math.abs(TYPE_ORDER.indexOf(a) - TYPE_ORDER.indexOf(b));
  return 3 - Math.min(d, 6 - d);
};

// A code shorter than three letters is filled with its first letter (Gore & Brown, 2006)
const fill = (code) => (code.length ? [...code, code[0], code[0]].slice(0, 3) : []);

/**
 * Brown & Gore's (1994) C index between the student's top three and a program's code, 0-18:
 * position by position, closeness on the hexagon times 3, 2 and 1.
 */
export const congruence = (top, code) => {
  const a = fill(top);
  const b = fill(code);
  if (!a.length || !b.length) return 0;
  return a.reduce((sum, t, i) => sum + WEIGHTS[i] * closeness(t, b[i]), 0);
};

// C index cut-offs: 14+ is a great match (listed), 12-13 a good one (only tops a short list up to
// five). With the O*NET codes every possible result has at least five programs at 12+ (checked
// against all 262,144 score patterns); on UIC's 841 results a median of 16 are great matches.
export const GREAT_FIT = 14;
export const GOOD_FIT = 12;

const tierOf = (score) => (score >= GREAT_FIT ? 'great' : score >= GOOD_FIT ? 'good' : score > 0 ? 'look' : null);

/**
 * Fit by program code: the school's or ANNA's default code. A program with no code falls back to
 * its old single tags, each read as a one-letter code (S becomes S-S-S) and the best one kept, so
 * it sits on the same 0-18 scale as coded programs. A one-letter code can't reach 18 (at most 15,
 * when the student's top three sit around that letter), so less certain programs rank a little
 * lower than equally close coded ones instead of jumping ahead of them.
 */
const codeFitOf = (course, top) => {
  const own = courseCode(course);
  if (own.length) {
    const score = congruence(top, own);
    return { score, tier: tierOf(score), matched: top.filter((t) => own.includes(t)), code: own };
  }
  const tags = courseTypes(course);
  const best = tags
    .map((tag) => ({ tag, score: congruence(top, [tag]) }))
    .reduce((a, b) => (b.score > a.score ? b : a), { tag: null, score: 0 });
  return {
    score: best.score,
    tier: tierOf(best.score),
    matched: top.filter((t) => tags.includes(t)),
    code: best.tag ? [best.tag] : [],
    fallback: true,
  };
};

/**
 * Fit of one course with the student's top three. With program codes (`byCode`) this
 * is the C index above. Without them it falls back to the old single-tag rule (3 points for
 * the first interest, 2 for the second, 1 for the third):
 *   Great match  - the first interest, and either another top interest or nothing else
 *   Good match   - the first interest among other areas, the second and third together,
 *                  or a single-area course in the second interest
 *   Worth a look - any other course that touches the top three
 * `score` sorts within and across tiers (tier first, then points).
 */
export const fitOf = (course, top, byCode = courseCode(course).length > 0) => {
  if (byCode) return codeFitOf(course, top);
  const types = courseTypes(course);
  const matched = top.filter((t) => types.includes(t));
  const points = matched.reduce((sum, t) => sum + WEIGHTS[top.indexOf(t)], 0);
  let tier = null;
  if (matched.length) {
    const first = matched.includes(top[0]);
    const focused = types.length === 1;
    if (first && (matched.length > 1 || focused)) tier = 'great';
    else if (first || points >= 3 || (focused && matched[0] === top[1])) tier = 'good';
    else tier = 'look';
  }
  return { score: tier ? TIER_RANK[tier] * 10 + points : 0, tier, matched };
};

export const TIER_LABEL = { great: 'Great match', good: 'Good match', look: 'Worth a look' };

/** "Fits your People and Thinking interests" */
export const reasonOf = (matched) => {
  const names = matched.map((t) => TYPES[t].everyday);
  if (!names.length) return 'Close to your interests';
  const list = names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  return `Fits your ${list} interest${names.length > 1 ? 's' : ''}`;
};

/** How many programs the chat shows first. */
export const TOP_COUNT = 5;

// With program codes, great matches offered at the student's school come before other great
// matches; below the great ones (and with the old tags) fit always comes first
const schoolFirst = (a, b) =>
  a.tier === 'great' && b.tier === 'great' ? Number(b.atSchool) - Number(a.atSchool) : Number(b.tier === 'great') - Number(a.tier === 'great');

/**
 * The orders a top three can be read in: tied scores (the same number) can swap places, so
 * 7-5-5 gives C-R-I and C-I-R. Without scores, only the given order.
 */
export const tiedOrders = (top, scores) => {
  if (!scores) return [top];
  const orders = [[...top]];
  [0, 1].forEach((i) => {
    if ((scores[top[i]] ?? -1) !== (scores[top[i + 1]] ?? -2)) return;
    orders.slice().forEach((order) => {
      const swapped = [...order];
      [swapped[i], swapped[i + 1]] = [swapped[i + 1], swapped[i]];
      if (!orders.some((o) => o.join() === swapped.join())) orders.push(swapped);
    });
  });
  return orders;
};

/** Best fit over every order a tied top three can be read in. */
const bestFitOf = (course, orders, byCode) =>
  orders.map((order) => fitOf(course, order, byCode)).reduce((best, fit) => (fit.score > best.score ? fit : best));

/**
 * The programs that fit the top three, best first: by fit, then offered at the student's school,
 * then in their strand, then name. With program codes only great matches (14+) are kept, offered
 * ones first, topped up with the next closest so there are always TOP_COUNT to show; with the old
 * single tags every course that touches the top three is kept.
 */
export const rankPrograms = ({ courses = [], top, strand = null, offeredNames = new Set(), scores = null }) => {
  const byCode = codesReady(courses);
  // Tied interests: score each program against every order the tie allows (codes only)
  const orders = byCode ? tiedOrders(top, scores) : [top];
  const rows = courses
    .map((course) => {
      const fit = bestFitOf(course, orders, byCode);
      const strands = courseStrands(course);
      return {
        course,
        ...fit,
        strands,
        inStrand: Boolean(strand && strands.includes(strand)),
        atSchool: offeredNames.has(normalizeName(course.name)),
      };
    })
    .filter((row) => row.tier)
    .sort(
      (a, b) =>
        (byCode ? schoolFirst(a, b) : 0) ||
        b.score - a.score ||
        Number(b.atSchool) - Number(a.atSchool) ||
        Number(b.inStrand) - Number(a.inStrand) ||
        String(a.course.name).localeCompare(String(b.course.name)),
    );
  if (!byCode) return rows;
  const fits = rows.filter((row) => row.tier === 'great');
  return fits.length >= TOP_COUNT ? fits : rows.slice(0, TOP_COUNT);
};

/** The courses a student in this strand usually takes, ranked by fit with their interests (no fit last). */
export const rankStrandPrograms = ({ courses = [], top, strand, offeredNames = new Set() }) =>
  courses
    .filter((course) => strand && courseStrands(course).includes(strand))
    .map((course) => ({
      course,
      ...fitOf(course, top, codesReady(courses)),
      strands: courseStrands(course),
      inStrand: true,
      atSchool: offeredNames.has(normalizeName(course.name)),
    }))
    .sort((a, b) => b.score - a.score || Number(b.atSchool) - Number(a.atSchool) || String(a.course.name).localeCompare(String(b.course.name)));

/** The per-type lists the Conversation record stores (same shape as before: { social: [courses] … }). */
export const recommendationByType = (courses, top) =>
  Object.fromEntries(top.map((t) => [t, courses.filter((c) => courseTypes(c).includes(t))]));

/** Best name match for "Search for a program": exact, then starts-with, then contains (case-insensitive). */
export const findCourse = (courses, query) => {
  const q = normalizeName(query).replace(/^(bs|ba|bachelor of( science in)?)\s+/, '');
  if (q.length < 2) return [];
  const scored = courses
    .map((course) => {
      const name = normalizeName(course.name);
      const bare = name.replace(/^(bs|ba|bachelor of( science in| arts in)?)\s+/, '');
      const rank = name === q || bare === q ? 0 : bare.startsWith(q) || name.startsWith(q) ? 1 : name.includes(q) ? 2 : 9;
      return { course, rank };
    })
    .filter((row) => row.rank < 9)
    .sort((a, b) => a.rank - b.rank || String(a.course.name).localeCompare(String(b.course.name)));
  return scored.slice(0, 3).map((row) => row.course);
};

// ---------- search ----------

// Words students type that the catalog spells differently: jobs and short forms
const ALIASES = {
  nurse: ['nursing'], nurses: ['nursing'],
  doctor: ['medicine', 'medical'], doctors: ['medicine', 'medical'], med: ['medicine', 'medical'],
  engineer: ['engineering'], engineers: ['engineering'],
  teacher: ['education'], teachers: ['education'], teaching: ['education'], teach: ['education'],
  it: ['information technology'], computer: ['computer', 'information technology'], computers: ['computer', 'information technology'],
  programmer: ['computer science', 'information technology'], programming: ['computer science', 'information technology'], coding: ['computer science', 'information technology'],
  psych: ['psychology'], psychologist: ['psychology'],
  accountant: ['accountancy', 'accounting'], accounting: ['accountancy'],
  architect: ['architecture'], architects: ['architecture'],
  pilot: ['aeronautical', 'aviation', 'aircraft'], pilots: ['aeronautical', 'aviation', 'aircraft'],
  chef: ['culinary', 'hospitality'], cook: ['culinary'], cooking: ['culinary'], baking: ['culinary'],
  police: ['criminology'], cop: ['criminology'], crime: ['criminology'], detective: ['criminology'],
  lawyer: ['law', 'legal', 'political science'], attorney: ['law', 'legal'],
  business: ['business', 'management', 'entrepreneurship'], entrepreneur: ['entrepreneurship', 'business'],
  artist: ['fine arts', 'arts'], art: ['arts', 'fine arts'], drawing: ['fine arts', 'multimedia'], design: ['design'], designer: ['design'],
  music: ['music'], musician: ['music'], singer: ['music'],
  dentist: ['dentistry'], pharmacist: ['pharmacy'], vet: ['veterinary'], veterinarian: ['veterinary'],
  physio: ['physical therapy'], therapist: ['therapy'],
  seaman: ['marine', 'maritime'], sailor: ['marine', 'maritime'], seafarer: ['marine', 'maritime'],
  hotel: ['hospitality', 'hotel'], tourism: ['tourism'], tour: ['tourism'],
  writer: ['communication', 'journalism', 'literature'], journalist: ['journalism', 'communication'],
  math: ['mathematics'], maths: ['mathematics'],
  social: ['social work', 'sociology'],
  farmer: ['agriculture', 'agricultural'], farming: ['agriculture', 'agricultural'],
  animator: ['animation', 'multimedia'], games: ['game', 'entertainment', 'multimedia'], gaming: ['game', 'entertainment', 'multimedia'],
};
const STOP = new Set(['bs', 'bsc', 'ab', 'ba', 'bachelor', 'bachelors', 'of', 'in', 'and', 'the', 'degree', 'program', 'course', 'major']);
const clean = (text) => String(text || '').toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
const words = (text) => clean(text).split(' ').filter((w) => w && !STOP.has(w));
const trigrams = (word) => {
  const padded = `  ${word} `;
  const out = new Set();
  for (let i = 0; i < padded.length - 2; i += 1) out.add(padded.slice(i, i + 3));
  return out;
};
const similarity = (a, b) => {
  const ta = trigrams(a);
  const tb = trigrams(b);
  let shared = 0;
  ta.forEach((t) => {
    if (tb.has(t)) shared += 1;
  });
  return (2 * shared) / (ta.size + tb.size);
};
const commonPrefix = (a, b) => {
  let n = 0;
  while (n < a.length && n < b.length && a[n] === b[n]) n += 1;
  return n;
};
/** How well one typed word fits one word of a program name. */
const wordScore = (token, word) => {
  if (word === token) return 3;
  if (word.startsWith(token)) return token.length >= 3 ? 2 : 0.6;
  if (token.startsWith(word) && word.length >= 4) return 1.5;
  if (commonPrefix(token, word) >= 4) return 1.3; // nurse / nursing
  if (token.length >= 4 && word.length >= 4 && similarity(token, word) >= 0.5) return 1; // a typo
  return 0;
};

/**
 * Programs for what a student typed: a name, part of one, a job ("nurse", "pilot") or a near
 * miss ("enginering"), best first. Empty when nothing comes close.
 */
export const searchCourses = (courses, query, limit = 8) => {
  const q = clean(query);
  if (q.length < 2) return [];
  const tokens = words(q);
  if (!tokens.length) return [];
  const aliasTokens = [...new Set(tokens.flatMap((t) => (ALIASES[t] || []).flatMap(words)))].filter((t) => !tokens.includes(t));
  return courses
    .map((course) => {
      const name = clean(course.name);
      const nameWords = words(name);
      if (!nameWords.length) return { course, score: 0 };
      let score = 0;
      tokens.forEach((t) => {
        score += Math.max(0, ...nameWords.map((w) => wordScore(t, w)));
      });
      aliasTokens.forEach((t) => {
        score += 0.9 * Math.max(0, ...nameWords.map((w) => wordScore(t, w)));
      });
      if (name === q || nameWords.join(' ') === tokens.join(' ')) score += 5;
      else if (name.includes(q)) score += 2;
      return { course, score };
    })
    .filter((row) => row.score >= 1)
    .sort((a, b) => b.score - a.score || String(a.course.name).localeCompare(String(b.course.name)))
    .slice(0, limit)
    .map((row) => row.course);
};

// ---------- one source for programs, fit, strands and the student's stage ----------
// The chat (RiasecResult), the Results page (CareerInterests, ProgramSheet), the student PDF and
// Explore programs all read programs and fit from here, so a program never gets two labels.

const offeredNamesOf = (offerings = []) =>
  new Set((Array.isArray(offerings) ? offerings : []).filter((o) => o && o.is_offered !== false).map((o) => normalizeName(o.course_name)));

/**
 * The programs a student may be shown: the catalog without duplicates and tracks (unless their
 * school offers one, programCatalog.js), with the school's own interest codes in place.
 */
export const catalogFor = ({ courses = [], offerings = [] } = {}) => {
  const offeredNames = offeredNamesOf(offerings);
  return { courses: withSchoolCodes(visibleCatalog(Array.isArray(courses) ? courses : [], offeredNames), offerings), offeredNames };
};

/** The ranked programs for a result: the one list the chat, the Results page and the PDF show. */
export const programsFor = ({ courses = [], top, scores = null, strand = null, offerings = [] } = {}) => {
  const { courses: shown, offeredNames } = catalogFor({ courses, offerings });
  return rankPrograms({ courses: shown, top, strand, offeredNames, scores });
};

/**
 * One program's fit for this student, scored the way programsFor scores it (program codes, tied
 * interests read every way): { score, tier, matched }. `course` may be a name; `courses` is the
 * catalog it came from (decides codes or old tags), `offerings` the school's own codes.
 */
export const fitForStudent = (course, { top, scores = null, courses = null, offerings = [] } = {}) => {
  const base = typeof course === 'string' ? { name: course } : course || {};
  const [withCode] = withSchoolCodes([base], offerings);
  const byCode = Array.isArray(courses) && courses.length ? codesReady(courses) : courseCode(withCode).length > 0;
  const orders = byCode ? tiedOrders(top, scores) : [top];
  return bestFitOf(withCode, orders, byCode);
};

/** What a saved program says on any list: its fit now, or "Saved from your chat"; never a "not a match". */
export const SAVED_NOTE = 'Saved from your chat';

/**
 * A saved program as a row: the ranked row when it's in the list, else its fit worked out the same
 * way (looked up in the catalog, an old name through its twin), else no tier and the saved note.
 */
export const savedRowFor = (savedName, { rows = [], courses = [], top, scores = null, offerings = [] } = {}) => {
  const key = normalizeName(savedName);
  const listed = rows.find((row) => normalizeName(row.course?.name ?? row.name) === key);
  if (listed) return listed;
  const course = courses.find((c) => normalizeName(c.name) === key)
    || courses.find((c) => normalizeName(c.name) === normalizeName(twinOf(savedName)))
    || { name: savedName };
  const fit = fitForStudent(course, { top, scores, courses, offerings });
  return { course, ...fit, strands: courseStrands(course), saved: true, note: fit.tier ? '' : SAVED_NOTE };
};

/**
 * Where the student is, from their section: 'junior' (Grade 7 to 10, choosing a strand next),
 * 'college' (Higher Education or Graduate School, already in a program) or 'senior' (Grade 11 and
 * 12, and anyone else: the strand and college-programs flow as before). Reads the chat's
 * user.current_section or the /auth/user/ fields.
 */
export const stageOf = (user) => {
  const section = user?.current_section || {};
  const type = section.department_type || user?.department_type || '';
  if (type === 'higher_education' || type === 'graduate_school') return 'college';
  if (type && type !== 'basic_education') return 'senior';
  const level = String(section.organizational_level_name || user?.organizational_level_name || user?.organizational_level || '').trim();
  const match = level.match(/^(?:grade\s*)?(\d{1,2})$/i) || level.match(/\bgrade\s*(\d{1,2})\b/i);
  const grade = match ? Number(match[1]) : null;
  if (grade && grade >= 7 && grade <= 10) return 'junior';
  return 'senior';
};

/**
 * The strands that fit the top three, best first (up to `limit`), then GAS as the open option:
 * [{ key, value, label, fits, matched, best, open }]. A strand counts when it's built on the first
 * or second interest; tied interests are read every way, like programs.
 */
export const strandsFor = ({ top, scores = null, clarity = 'clear', limit = 2 } = {}) => {
  const gas = STRAND_BY_KEY.GAS;
  const open = { ...gas, matched: [], best: false, open: true };
  if (!Array.isArray(top) || !top.length || clarity === 'flat' || clarity === 'low') return [open];
  const orders = tiedOrders(top, scores);
  const rows = STRANDS.filter((s) => Array.isArray(s.fits))
    .map((s, index) => ({
      ...s,
      index,
      score: Math.max(...orders.map((order) => order.reduce((sum, t, i) => sum + (s.fits.includes(t) ? WEIGHTS[i] : 0), 0))),
      matched: top.filter((t) => s.fits.includes(t)),
    }))
    .filter((s) => s.score >= 2)
    .sort((a, b) => b.score - a.score || b.matched.length - a.matched.length || a.fits.length - b.fits.length || a.index - b.index)
    .slice(0, limit)
    .map((s, i) => ({ key: s.key, value: s.value, label: s.label, fits: s.fits, matched: s.matched, best: i === 0, open: false }));
  return [...rows, open];
};

// Section names that say the program: "BSIT 1A", "BSA 2-B", "BSN-3"
const PROGRAM_SHORT = {
  BSIT: 'information technology', BSCS: 'computer science', BSIS: 'information systems', BSEMC: 'entertainment multimedia computing',
  BSA: 'accountancy', BSAIS: 'accounting information system', BSMA: 'management accounting', BSN: 'nursing',
  BSPSYCH: 'psychology', BSPSY: 'psychology', ABPSYCH: 'psychology', BSCRIM: 'criminology', BSHM: 'hospitality management',
  BSTM: 'tourism management', BSCE: 'civil engineering', BSME: 'mechanical engineering', BSEE: 'electrical engineering',
  BSECE: 'electronics engineering', BSCPE: 'computer engineering', BSARCH: 'architecture', BSPHARMA: 'pharmacy',
  BSMT: 'medical laboratory science', BSMLS: 'medical laboratory science', BSPT: 'physical therapy', BSSW: 'social work',
  BEED: 'elementary education', BECED: 'early childhood education', BPED: 'physical education', BSBIO: 'biology',
  BSENTREP: 'entrepreneurship', ABCOMM: 'communication', ABPOLSCI: 'political science',
};

/**
 * The program a college student is in, read from their section's name ("BSIT 1A") or a full
 * program name in it; null when it can't be told. Only a clear match counts.
 */
export const programOfSection = (user, courses = []) => {
  const section = user?.current_section || {};
  const name = String(section.section_name || user?.section_name || '').trim();
  if (!name || !courses.length) return null;
  const full = courses
    .filter((c) => c?.name && normalizeName(name).includes(normalizeName(c.name)))
    .sort((a, b) => b.name.length - a.name.length)[0];
  if (full) return full;
  const short = (name.toUpperCase().match(/^[A-Z][A-Z-]*/) || [''])[0].replace(/-/g, '');
  const words = PROGRAM_SHORT[short];
  if (!words) return null;
  return courses.find((c) => normalizeName(c.name).endsWith(words) && !isHiddenProgram(c.name)) || null;
};
